import { useState } from 'react'
import Select from 'react-select'
import { Controller, useFormContext } from '@astro/forms'
import { getErrorMessage } from './fields'
import { selectStyles } from './SelectField'
import { UPI_HANDLES } from './registerSteps'

const OTHER = '__other__'

const HANDLE_OPTIONS = [
  { value: OTHER, label: 'Other — type your own handle', short: 'Other' },
  ...UPI_HANDLES.map((h) => ({ ...h, short: `@${h.value}` })),
]

/* Borderless select embedded as the right segment of the input group. */
const embeddedStyles = {
  ...selectStyles,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    minWidth: 118,
  }),
  valueContainer: (base) => ({ ...base, padding: '0 0 0 10px' }),
  placeholder: (base) => ({ ...base, color: '#b5a896', fontSize: 13, whiteSpace: 'nowrap' }),
  singleValue: (base) => ({ ...base, color: '#b45309', fontWeight: 700, fontSize: 13.5 }),
  input: (base) => ({ ...base, color: '#1c0a04', fontSize: 13.5 }),
  dropdownIndicator: (base) => ({ ...base, color: '#b45309', padding: '0 8px 0 2px' }),
  menu: (base) => ({ ...selectStyles.menu(base), minWidth: 280 }),
}

/**
 * One input group per the design reference: UPI name on the left, provider
 * handle as an attached dropdown on the right. Picking "Other" swaps the
 * dropdown for a small "@handle" text box (with a way back to the list).
 */
export default function UpiField() {
  const { register, control, formState: { errors } } = useFormContext()
  const [isOther, setIsOther] = useState(false)
  const error = getErrorMessage(errors, 'upi_name') ?? getErrorMessage(errors, 'upi_handle')

  return (
    <div>
      <label className="ar-label">
        UPI ID <span className="normal-case font-normal text-stone-400">(optional)</span>
      </label>
      <div
        className={`flex items-stretch rounded-xl border-[1.5px] bg-[#FAFAF9] transition-all focus-within:bg-white focus-within:border-goldAccent/55 focus-within:shadow-[0_0_0_3px_rgba(217,119,6,.09)] ${
          error ? 'border-red-400/50' : 'border-goldAccent/20'
        }`}
      >
        <input
          type="text"
          placeholder="yourname"
          {...register('upi_name', {
            pattern: { value: /^[\w.-]+$/, message: 'Only letters, numbers, dots and dashes.' },
            validate: (v, formValues) =>
              !formValues.upi_handle || !!v || 'Enter the UPI name for the selected provider.',
          })}
          className="flex-1 min-w-0 bg-transparent outline-none focus-visible:outline-none px-3.5 py-[11px] text-[13.5px] text-[#1C0A04] placeholder:text-[#B5A896]"
        />
        <div className="w-px bg-goldAccent/20 my-2" aria-hidden="true" />
        <Controller
          name="upi_handle"
          control={control}
          rules={{
            validate: (v, formValues) =>
              !formValues.upi_name || !!v || 'Select your UPI provider handle.',
          }}
          render={({ field }) =>
            isOther ? (
              <div className="flex items-center">
                <span className="pl-2.5 text-[13.5px] font-bold text-goldAccent">@</span>
                <input
                  type="text"
                  placeholder="yourbank"
                  autoFocus
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value.replace(/[^\w-]/g, ''))}
                  onBlur={field.onBlur}
                  className="w-24 bg-transparent outline-none focus-visible:outline-none py-[11px] pl-0.5 text-[13.5px] text-[#1C0A04] placeholder:text-[#B5A896]"
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsOther(false)
                    field.onChange('')
                  }}
                  className="px-2 text-stone-400 hover:text-goldAccent text-sm"
                  aria-label="Choose provider from list"
                  title="Choose from list"
                >
                  ▾
                </button>
              </div>
            ) : (
              <Select
                inputId="select-upi_handle"
                options={HANDLE_OPTIONS}
                value={HANDLE_OPTIONS.find((o) => o.value === field.value) ?? null}
                onChange={(option) => {
                  if (option?.value === OTHER) {
                    setIsOther(true)
                    field.onChange('')
                  } else {
                    field.onChange(option?.value ?? '')
                  }
                }}
                onBlur={field.onBlur}
                placeholder="@provider"
                styles={embeddedStyles}
                // Full "@ybl — PhonePe (Yes Bank)" rows in the menu, compact "@ybl" once picked.
                formatOptionLabel={(option, { context }) =>
                  context === 'value' ? option.short : option.label
                }
                // Keep "Other" reachable no matter what the user searches.
                filterOption={(candidate, input) =>
                  candidate.value === OTHER ||
                  candidate.label.toLowerCase().includes(input.toLowerCase())
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )
          }
        />
      </div>
      {error && <p className="ar-error">{error}</p>}
    </div>
  )
}
